/**
 * Editorial-question teaser features and shared widgets: the embedded card a
 * partner site shows to draw readers into a Question instance.
 *
 * Registers `EditorialQuestionFeature` (the teaser UI), which takes its colors
 * from the active theme's entry-point tokens, so a partner restyles the teaser
 * through its theme rather than through a feature.
 * `AdaptiveLayoutQuestionTeaser` switches between narrow and wide layouts at a
 * 540px breakpoint, both rendering inside `<TeaserScreen>` so partners can drop
 * the same iframe into different column widths and get the right layout
 * automatically.
 *
 * The teaser variants read the question text, preview comment, and comment
 * count from the `spaces.teaser` payload (`useCommentTeaser`). The CMS-embedded
 * variant (`CMSEmbeddedQuestionTeaserFeature`, for partners whose CMS already
 * owns the question prompt) reads the ambient instance's teaser; the article
 * teaser and the video-overlay promo take a space key and preview that space
 * rather than the space they render inside.
 * `QuestionVideoOverlayPromo` and the wide-overlay widget exist for
 * video-voting promos and accept an extra
 * `promoTopRightWidgets` slot from `VideoVotingConfig` so video features can
 * layer their own controls into the same teaser shell.
 */

import { AddComment, Edit } from '@carbon/icons-react';
import { keyToUrl } from '@repo/shared/keyutil';
import { displayTextOf } from '@repo/shared/orpc/multi-language';
import type { CommentTeaserPreview } from '@repo/shared/orpc/procedures/spaces';
import { isoToMs } from '@repo/shared/orpc/schemas';
import type { UserProfile } from '@repo/shared/orpc/types';
import { parseHexColor, useTheme } from '@repo/theme';
import { useIsBeta } from 'component/admin';
import { AdaptiveWidth, FlowBox, HorizBox, HoverView, Pad, PadBox } from 'component/basics';
import { CardStack } from 'component/card-stack';
import { Byline } from 'component/people';
import { SkeletonCommentInCardStack } from 'component/progress/skeleton-comment';
import { RichText } from 'component/richtext';
import { ExperimentTag } from 'component/tag/experiment-tag';
import { Heading, UtilityText } from 'component/text';
import { useLanguage } from 'component/translation';
import { useCommentTeaser } from 'data/spaces';
import { useStory } from 'data/stories';
import React, { useState } from 'react';
import type { DimensionValue } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import type { ArticleConfig } from 'structure/article';
import { TeaserScreen } from 'system/teaser';
import { useIsAdmin } from 'util/admin';
import { useDatastore, useGlobalParams, useInstanceKey, useTrackEvent } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import type { Feature, TLabel } from 'util/stdtypes';
import { makeTestID, t } from 'util/stdtypes';

export type ArticleTeaserConfig = {
    teaserSingleQuestion: React.ComponentType<{ spaceKey: string }>;
    additionalAdminTeaserWidgets: React.ComponentType[];
};

export const EditorialQuestionFeature: Feature<ArticleConfig, ArticleTeaserConfig> = {
    name: 'Connected Question',
    key: 'editorialquestion',
    providesConfig: {
        teaser: EditorialQuestionTeaser,
    },
    receivesConfigDefaults: {
        teaserSingleQuestion: AdaptiveLayoutArticleQuestionTeaserWithComment,
        additionalAdminTeaserWidgets: [],
    },
};

export type CMSTeaserConfig = {
    teaserSingleQuestion: React.ComponentType<{
        questionText: string;
        questionKey: string;
        entryUrl: string;
        commentPreview: CommentTeaserPreview | null;
        commentCount: number;
    }>;
};

export const CMSEmbeddedQuestionTeaserFeature: Feature<ArticleConfig, CMSTeaserConfig> = {
    name: 'Question Teaser (CMS)',
    key: 'cmsembeddedquestionteaser',
    dev: true,
    providesConfig: {
        teaser: AdaptiveLayoutCMSEmbeddedQuestionTeaser,
    },
    receivesConfigDefaults: {
        teaserSingleQuestion: AdaptiveLayoutQuestionTeaser,
    },
};

export function EditorialQuestionTeaser() {
    const isAdmin = useIsAdmin();
    const instanceKey = useInstanceKey();
    const spaceKeys = useStory(instanceKey)?.spaceKeys ?? [];
    const { teaserSingleQuestion } = useConfig<ArticleTeaserConfig>();
    const url = keyToUrl(instanceKey);
    useTrackEvent('teaser-show', { url });

    if (spaceKeys.length >= 1) {
        return <TeaserScreen>{React.createElement(teaserSingleQuestion, { spaceKey: spaceKeys[0] })}</TeaserScreen>;
    } else if (isAdmin) {
        return (
            <TeaserScreen>
                <AdminTeaserAction />
            </TeaserScreen>
        );
    } else {
        return <TeaserScreen />;
    }
}

function AdminTeaserAction() {
    const { additionalAdminTeaserWidgets } = useConfig<ArticleTeaserConfig>();
    return (
        <FlowBox columnGap={10}>
            {additionalAdminTeaserWidgets?.map((Widget, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Widget key={i} />
            ))}
        </FlowBox>
    );
}

export function AdaptiveLayoutCMSEmbeddedQuestionTeaser() {
    const questionKey = useInstanceKey();
    const { entryUrl } = useGlobalParams();
    const { teaserSingleQuestion } = useConfig<CMSTeaserConfig>();
    const { name: questionText, preview: commentPreview, topLevelCount } = useCommentTeaser();
    return (
        <TeaserScreen>
            {React.createElement(teaserSingleQuestion, {
                questionText,
                questionKey,
                entryUrl,
                commentPreview,
                commentCount: topLevelCount,
            })}
        </TeaserScreen>
    );
}

export type AdaptiveLayoutArticleQuestionTeaserWithCommentProps = { spaceKey: string };

export function AdaptiveLayoutArticleQuestionTeaserWithComment({ spaceKey }: AdaptiveLayoutArticleQuestionTeaserWithCommentProps) {
    const instanceKey = useInstanceKey();
    const entryUrl = keyToUrl(instanceKey);

    const { name: questionText, preview: commentPreview, topLevelCount: commentCount } = useCommentTeaser(spaceKey);
    const isAdmin = useIsAdmin();

    return (
        <>
            <AdaptiveLayoutQuestionTeaser
                commentCount={commentCount}
                commentPreview={commentPreview}
                entryUrl={entryUrl}
                questionKey={spaceKey}
                questionText={questionText}
            />
            {isAdmin && (
                <PadBox top={20}>
                    <AdminTeaserAction />
                </PadBox>
            )}
        </>
    );
}

export type AdaptiveLayoutQuestionTeaserProps = {
    questionText: string;
    questionKey: string;
    entryUrl: string;
    commentPreview: any;
    commentCount: any;
};

export function AdaptiveLayoutQuestionTeaser({
    questionText,
    questionKey,
    entryUrl,
    commentPreview,
    commentCount,
}: AdaptiveLayoutQuestionTeaserProps) {
    const [narrow, setNarrow] = useState(false);
    const [hovered, setHovered] = useState(false);
    const datastore = useDatastore();

    function onPress() {
        const globalParams = { entryUrl };
        datastore.gotoInstance({ structureKey: 'question', instanceKey: questionKey, globalParams });
    }

    return (
        <AdaptiveWidth breakpoint={540} setNarrow={setNarrow}>
            <Pressable onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)} onPress={onPress} testID='question-teaser'>
                {narrow ? (
                    <NarrowQuestionTeaserWithComment
                        commentCount={commentCount}
                        commentPreview={commentPreview}
                        hovered={hovered}
                        questionText={questionText}
                    />
                ) : (
                    <WideQuestionTeaserWithComment
                        commentCount={commentCount}
                        commentPreview={commentPreview}
                        hovered={hovered}
                        questionText={questionText}
                    />
                )}
            </Pressable>
        </AdaptiveWidth>
    );
}

export type NarrowQuestionTeaserWithCommentProps = {
    questionText: string;
    commentPreview: any;
    commentCount: any;
    onPress?: () => void;
    hovered?: boolean;
};

export function NarrowQuestionTeaserWithComment({
    questionText,
    commentPreview,
    commentCount,
    onPress,
    hovered,
}: NarrowQuestionTeaserWithCommentProps) {
    const s = NarrowQuestionTeaserStyle;
    const { colors } = useTheme();
    return (
        <View style={[s.top, { backgroundColor: colors.colorEntryPointColorGreen }]}>
            <PadBox horiz={20} vert={20}>
                <QuestionInfo commentCount={commentCount} hovered={hovered} narrow questionText={questionText} />
            </PadBox>
            <View style={[s.bottom, { backgroundColor: colors.colorEntryPointColorGreenTint }]}>
                <PadBox bottom={20}>
                    <CommentStack commentPreview={commentPreview} />
                </PadBox>
                <TeaserButton
                    backgroundColor={colors.colorButtonTeaser}
                    icon={<Edit color={colors.colorTextOnTeaserButton} />}
                    label={t('editorial_question.share_thoughts', 'Share your thoughts')}
                    onPress={onPress}
                    textColor={colors.colorTextOnTeaserButton}
                    wide
                />
            </View>
        </View>
    );
}

const NarrowQuestionTeaserStyle = StyleSheet.create({
    top: {
        borderRadius: 10,
    },
    bottom: {
        padding: 20,
        flex: 1,
        justifyContent: 'center',
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 10,
    },
});

export type WideQuestionTeaserWithCommntProps = {
    questionText: string;
    commentPreview: any;
    commentCount: any;
    onPress?: () => void;
    hovered?: boolean;
};

export function WideQuestionTeaserWithComment({
    questionText,
    commentPreview,
    commentCount,
    onPress,
    hovered,
}: WideQuestionTeaserWithCommntProps) {
    const s = WideQuestionTeaserStyle;
    const { colors } = useTheme();
    return (
        <View style={s.outer}>
            <View style={[{ backgroundColor: colors.colorEntryPointColorGreen }, s.left]}>
                <QuestionInfo commentCount={commentCount} hovered={hovered} questionText={questionText} />
                <Pad size={20} />
                <TeaserButton
                    backgroundColor={colors.colorButtonTeaser}
                    icon={<Edit color={colors.colorTextOnTeaserButton} />}
                    label={t('editorial_question.share_thoughts', 'Share your thoughts')}
                    onPress={onPress}
                    textColor={colors.colorTextOnTeaserButton}
                />
            </View>
            <View style={[{ backgroundColor: colors.colorEntryPointColorGreenTint }, s.right]}>
                <CommentStack commentPreview={commentPreview} />
            </View>
        </View>
    );
}

const WideQuestionTeaserStyle = StyleSheet.create({
    outer: {
        borderRadius: 10,
        flexDirection: 'row',
    },
    left: {
        padding: 24,
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        flex: 1,
    },
    right: {
        padding: 24,
        flex: 1,
        justifyContent: 'center',
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
    },
});

export type CommentStackProps = { commentPreview: CommentTeaserPreview | null; width?: DimensionValue };

export function CommentStack({ commentPreview, width }: CommentStackProps) {
    const isoLocale = useLanguage();

    if (!commentPreview) {
        // Doubles as the standing empty-conversation visual on partner
        // pages, not just a loading state — a perpetual sweep would be noise.
        return <SkeletonCommentInCardStack isLoading={false} width={width} />;
    }
    const previewAuthorProfile: UserProfile = {
        profileKey: commentPreview.authorKey,
        name: commentPreview.authorName ?? '',
        photoUrl: commentPreview.authorPhoto,
    };

    return (
        <CardStack width={width}>
            <PadBox horiz={20} vert={16}>
                <Byline
                    clickable={false}
                    originalLanguage={commentPreview.originalLanguage}
                    profile={previewAuthorProfile}
                    time={isoToMs(commentPreview.createdAt)}
                    type='small'
                />
                <Pad size={12} />
                <RichText numberOfLines={2} text={displayTextOf(commentPreview.text, isoLocale)} />
            </PadBox>
        </CardStack>
    );
}

export type QuestionInfoProps = { questionText: string; commentCount: number; narrow?: boolean; questionLabel?: TLabel; hovered?: boolean };

export function QuestionInfo({
    questionText,
    commentCount,
    narrow = false,
    hovered,
    questionLabel = t('editorial_question.lets_talk', "Let's talk about...") as TLabel,
}: QuestionInfoProps) {
    const showBetaTag = useIsBeta();
    const textColor = useTheme().colors.colorTextOnEntryPointColorBg;

    // Special implementation of toHoverColor that allows for the background
    // color to bleed through.
    const toTransparentHoverColor = (textColor: string) => {
        const { r, g, b } = parseHexColor(textColor);
        return `rgba(${r}, ${g}, ${b}, 0.7)`;
    };

    const headingColor = hovered ? toTransparentHoverColor(textColor) : textColor;
    return (
        <View>
            <HorizBox center>
                <AddComment color={textColor} />
                <Pad size={8} />
                <UtilityText caps color={textColor} label={questionLabel} type='tiny' weight='regular' />
                {showBetaTag && (
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Pad size={8} />
                        <ExperimentTag accent label={t('tag.beta', 'Beta')} />
                    </View>
                )}
            </HorizBox>
            <Pad size={narrow ? 20 : 24} />
            <Heading color={headingColor} text={questionText} type='large' weight='medium'></Heading>
            <Pad size={8} />
            {commentCount ? (
                <UtilityText
                    color={textColor}
                    formatParams={{
                        count: commentCount,
                        singular: t('editorial_question.response_singular', 'response'),
                        plural: t('editorial_question.responses_plural', 'responses'),
                    }}
                    label={t('editorial_question.count_noun', '{count} {noun}')}
                    type='tiny'
                    weight='medium'
                />
            ) : (
                <UtilityText
                    color={textColor}
                    label={t('editorial_question.be_first', 'Be the first to share your thoughts')}
                    type='tiny'
                    weight='medium'
                />
            )}
        </View>
    );
}

export type TeaserButtonProps = {
    label?: TLabel;
    text?: string;
    icon?: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
    onPress?: () => void;
    wide?: boolean;
    backgroundColor?: string;
    textColor?: string;
};

export function TeaserButton({ label, text, icon, onPress, wide, backgroundColor, textColor }: TeaserButtonProps) {
    const s = TeaserButtonStyle;
    const buttonStyle = [s.button, { backgroundColor }, wide && s.wideStyle];
    return (
        <HoverView onPress={onPress} style={buttonStyle} testID={makeTestID({ label, text })}>
            <UtilityText color={textColor} label={label} text={text} type='large' />
            {icon && <PadBox left={12}>{icon}</PadBox>}
        </HoverView>
    );
}
const TeaserButtonStyle = StyleSheet.create({
    button: {
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 32,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        height: 40,
        alignItems: 'center',
    },
    wideStyle: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        flex: 1,
    },
});

type VideoVotingConfig = {
    promoTopRightWidgets: React.ComponentType[];
};

type WideQuestionVideoOverlayPromoWithCommentProps = {
    questionText: string;
    commentPreview: any;
    commentCount: any;
    onPress: () => void;
};

function WideQuestionVideoOverlayPromoWithComment({
    questionText,
    commentPreview,
    commentCount,
    onPress,
}: WideQuestionVideoOverlayPromoWithCommentProps) {
    const s = WideQuestionTeaserStyle;
    const { promoTopRightWidgets } = useConfig<VideoVotingConfig>();
    const { colors } = useTheme();
    return (
        <View style={s.outer}>
            <View style={[{ backgroundColor: colors.colorEntryPointColorGreen }, s.left]}>
                <QuestionInfo
                    commentCount={commentCount}
                    questionLabel={t('editorial_question.join_discussion', 'Join the discussion')}
                    questionText={questionText}
                />
                <Pad size={20} />
                <TeaserButton
                    backgroundColor={colors.colorButtonTeaser}
                    icon={<Edit color={colors.colorTextOnTeaserButton} />}
                    label={t('editorial_question.share_thoughts', 'Share your thoughts')}
                    onPress={onPress}
                    textColor={colors.colorTextOnTeaserButton}
                />
            </View>
            <View style={[{ backgroundColor: colors.colorEntryPointColorGreenTint }, s.right, { paddingTop: 12 }]}>
                <HorizBox center gap={8} right>
                    {promoTopRightWidgets?.map((Widget, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Widget key={i} />
                    ))}
                </HorizBox>
                <Pad size={12} />
                <CommentStack commentPreview={commentPreview} />
            </View>
        </View>
    );
}

export type QuestionVideoOverlayPromoProps = { spaceKey: string };

export function QuestionVideoOverlayPromo({ spaceKey }: QuestionVideoOverlayPromoProps) {
    const instanceKey = useInstanceKey();
    const entryUrl = keyToUrl(instanceKey);
    const datastore = useDatastore();

    const { name: questionText, preview: commentPreview, topLevelCount: commentCount } = useCommentTeaser(spaceKey);
    function onPress() {
        const globalParams = { entryUrl };
        datastore.gotoInstance({ structureKey: 'question', instanceKey: spaceKey, globalParams });
    }

    return (
        <Pressable onPress={onPress} testID='question-promo'>
            <WideQuestionVideoOverlayPromoWithComment
                commentCount={commentCount}
                commentPreview={commentPreview}
                onPress={onPress}
                questionText={questionText}
            />
        </Pressable>
    );
}
